/**
 * Configuração da revenda (D13) — `tenant_settings` pela API.
 *
 * ── Tudo opcional no PATCH, e o motivo ────────────────────────────────────
 *
 * A tela de configuração tem seções independentes, e salvar uma não pode
 * apagar as outras. Com todos os campos opcionais, o corpo carrega só o que
 * mudou — e `.strict()` garante que um nome errado seja recusado em vez de
 * silenciosamente ignorado, que é como uma configuração "salva" nunca entra em
 * vigor.
 *
 * ── O que NÃO está aqui, de propósito ─────────────────────────────────────
 *
 * `exigir_mfa` mora em `tenant_settings` e é da B15, com rota própria
 * (`PATCH /v1/security/mfa`) que exige justificativa e escreve em audit_logs.
 * Expor o mesmo campo aqui criaria um segundo caminho, sem justificativa, para
 * desligar o segundo fator da revenda inteira.
 */

import { z } from 'zod';
import { InstanteSchema } from './valores.js';

/** `HH:MM` em 24 horas. */
const HoraSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM, de 00:00 a 23:59.');

export const JanelaDeAtendimentoSchema = z
  .object({
    /** 0 = domingo, como `Date.getDay()` e `EXTRACT(dow)` do Postgres. */
    diaDaSemana: z.number().int().min(0).max(6),
    abre: HoraSchema,
    fecha: HoraSchema,
  })
  .strict();

/**
 * Quando o sistema abre negócio sozinho (C17).
 *
 * A regra mais perigosa do produto: mal configurada, cria uma oportunidade por
 * mensagem ou nenhuma. É por ela que existe a simulação.
 */
export const RegraDeOportunidadeSchema = z
  .object({
    /** Canais que disparam. Vazio = a regra está desligada. */
    canais: z.array(z.string().min(1)).optional(),
    /**
     * Quais etapas contam como "em aberto".
     *
     * Usada em três lugares: a regra não abre negócio novo para quem já tem um
     * aberto (C17), o funil sabe o que é terminal (D04), e o dashboard conta
     * fechamento (D07). Mudar aqui muda os três.
     */
    etapas_abertas: z.array(z.string().min(1)).optional(),
    /**
     * Quantos dias atrás procurar um negócio ABERTO antes de criar outro.
     *
     * ── Por que este campo faltava, e o que isso quebrava ─────────────────
     *
     * O worker da C17 lê `janela_dias` desde sempre (`regra-oportunidade.ts`,
     * com 30 de padrão), e o provisionamento o grava. A schema não o
     * declarava — e ela é `.strict()`.
     *
     * O cliente valida TODA resposta contra a schema da rota. Resultado: a
     * seção inteira de Configuração era recusada antes de chegar à tela, em
     * qualquer revenda cuja regra tivesse o campo. Medido contra o valor real
     * do staging em 10/09: `unrecognized_keys: ["janela_dias"]`.
     *
     * É o mesmo defeito das etapas do funil, de outro jeito: o servidor manda
     * um campo verdadeiro, e o contrato recusa a resposta inteira por causa
     * dele. Aqui ninguém tinha notado porque a seção abre pouco.
     */
    janela_dias: z.number().int().min(1).max(365).optional(),
    /** Dias sem interação a partir dos quais o cartão aparece em risco (D04). */
    risco: z
      .object({
        dias_sem_interacao: z.number().int().min(1).max(365).optional(),
        dias_na_mesma_etapa: z.number().int().min(1).max(365).optional(),
      })
      .strict()
      .optional(),
    /**
     * Aceito e IGNORADO desde a issue #489.
     *
     * Ele prometia estreitar a regra: abrir oportunidade só quando a conversa
     * demonstrasse interesse num veículo. O sinal nunca existiu — o CRM não
     * tem `vehicle_interest` (lacuna documentada desde a C11) — e o worker,
     * certo em não criar o que não consegue confirmar, devolvia sempre "não
     * abre". Ligar a opção DESLIGAVA a criação automática, em silêncio.
     *
     * Um controle que faz o contrário do que diz é pior que controle nenhum, e
     * este ainda nomeava um ramo só. A opção saiu da tela e o worker parou de
     * ler o campo.
     *
     * ── Por que ele continua no schema ──────────────────────────────────
     *
     * `RegraDeOportunidadeSchema` é `.strict()`: tirar o campo faria toda
     * configuração já gravada com ele ser recusada na leitura, e isso é quebra
     * de contrato com migração — preço alto para remover algo que nenhuma
     * organização tem ligado (medido em 22/09/2026: quatro organizações, uma
     * com `false` e três sem regra).
     *
     * Quando o sinal existir, o campo volta a valer já genérico — "interesse
     * no produto ou serviço" —, e aí a decisão é sobre o nome, não sobre
     * quebrar leitura de quem está de pé.
     */
    exige_interesse_em_veiculo: z.boolean().optional(),
  })
  .strict();

/** O que acontece ao encerrar conversa (D08). */
export const RegraDeEncerramentoSchema = z
  .object({
    criar_tarefa_de_proxima_acao: z.boolean().optional(),
    titulo: z.string().min(1).max(200).optional(),
    prazo_em_horas: z.number().int().min(1).max(8_760).optional(),
  })
  .strict();

/**
 * Quem mexeu por último, quando, e com qual justificativa.
 *
 * ── Por que isto passou a sair na resposta ────────────────────────────────
 *
 * A tela dizia "alterar exige justificativa, que fica na auditoria" e não
 * mostrava justificativa nenhuma. Promessa sozinha não se verifica — e é o
 * registro à vista que faz a próxima pessoa escrever um motivo de verdade em
 * vez de "ajuste".
 *
 * `por` é `null` quando quem alterou não é mais membro da revenda: o nome vem
 * de `core.users` por `memberships`, e alguém que saiu não tem mais linha.
 * Guardar o nome dentro do log resolveria a busca e criaria outro problema —
 * um nome congelado que não acompanha correção de cadastro.
 */
export const UltimaAlteracaoSchema = z
  .object({
    por: z.string().nullable(),
    em: InstanteSchema,
    justificativa: z.string().nullable(),
  })
  .strict();

export type UltimaAlteracao = z.infer<typeof UltimaAlteracaoSchema>;

export const ConfiguracaoDoTenantSchema = z.object({
  timezone: z.string(),
  horario_de_atendimento: z.array(JanelaDeAtendimentoSchema),
  criar_oportunidade_regra: RegraDeOportunidadeSchema,
  encerramento_regra: RegraDeEncerramentoSchema,
  limiar_match_probabilistico: z.number(),
  usar_documento_na_identidade: z.boolean(),
  atualizado_em: InstanteSchema,
  /** `null` numa revenda cuja configuração nunca foi alterada pela tela. */
  ultima_alteracao: UltimaAlteracaoSchema.nullable(),
});

export const AlterarConfiguracaoSchema = z
  .object({
    timezone: z.string().min(1).max(64).optional(),
    horario_de_atendimento: z.array(JanelaDeAtendimentoSchema).max(50).optional(),
    criar_oportunidade_regra: RegraDeOportunidadeSchema.optional(),
    encerramento_regra: RegraDeEncerramentoSchema.optional(),
    /**
     * De 0 a 1. Mudar NÃO retroage.
     *
     * A resposta diz isso com todas as letras: quem baixa o limiar espera ver
     * duplicatas antigas se juntarem, e não é o que acontece — a escada só
     * decide no momento em que o contato chega. Sem o aviso, a pessoa mexe de
     * novo achando que não pegou.
     */
    limiar_match_probabilistico: z.number().min(0).max(1).optional(),
    usar_documento_na_identidade: z.boolean().optional(),
    /** Obrigatória: configuração alterada é a primeira suspeita num incidente. */
    justificativa: z.string().min(10).max(500),
  })
  .strict();

export type ConfiguracaoDoTenant = z.infer<typeof ConfiguracaoDoTenantSchema>;
export type AlterarConfiguracao = z.infer<typeof AlterarConfiguracaoSchema>;

export const ConfiguracaoSalvaSchema = z.object({
  configuracao: ConfiguracaoDoTenantSchema,
  /**
   * O que a mudança NÃO faz, em palavras de quem configura.
   *
   * Não é enfeite: a §14.3 proíbe deixar o usuário achar que aconteceu mais do
   * que aconteceu, e "não retroage" é a expectativa errada mais comum aqui.
   */
  avisos: z.array(z.string()),
});

export type ConfiguracaoSalva = z.infer<typeof ConfiguracaoSalvaSchema>;

/* ── Simulação ───────────────────────────────────────────────────────────── */

/**
 * Rodar a regra nova contra o passado recente, antes de salvar.
 *
 * A Técnica da D13: *"a regra de abertura de negócio é a mais perigosa: mal
 * configurada, cria oportunidade a cada mensagem ou nenhuma. Simulação antes de
 * salvar — rodar a regra nova contra os últimos N eventos e mostrar quantas
 * oportunidades teria criado."*
 */
export const SimularRegraSchema = z
  .object({
    criar_oportunidade_regra: RegraDeOportunidadeSchema,
    /** Janela olhada para trás. Padrão 30 dias. */
    dias: z.number().int().min(1).max(90).optional(),
  })
  .strict();

export const SimulacaoDaRegraSchema = z.object({
  dias: z.number().int(),
  /** Conversas que receberam mensagem do cliente num canal da regra. */
  conversas_no_periodo: z.number().int(),
  /** Clientes distintos por trás delas — o teto de oportunidades novas. */
  clientes_distintos: z.number().int(),
  /**
   * Quantas oportunidades a regra teria aberto, ESTIMADO.
   *
   * Estimado, e a palavra é literal: a regra não abre negócio para quem já tem
   * um em etapa aberta, e "já tinha" depende do estado NAQUELE momento — que
   * não é reconstituível sem replay da timeline inteira. O cálculo usa o estado
   * de agora, então erra para MENOS quando uma oportunidade foi fechada no
   * meio do período.
   */
  oportunidades_estimadas: z.number().int(),
  /** O que a simulação não consegue afirmar, para a tela mostrar junto. */
  ressalvas: z.array(z.string()),
});

export type SimularRegra = z.infer<typeof SimularRegraSchema>;
export type SimulacaoDaRegra = z.infer<typeof SimulacaoDaRegraSchema>;

/* ── Feature flags ───────────────────────────────────────────────────────── */

export const FlagDoTenantSchema = z.object({
  chave: z.string(),
  descricao: z.string(),
  /** `false` também quando existe linha sem homologação — ver a nota do PATCH. */
  ligada: z.boolean(),
  permanente: z.boolean(),
  homologado_em: InstanteSchema.nullable(),
  homologado_por: z.string().nullable(),
});

export const FlagsDoTenantSchema = z.object({ itens: z.array(FlagDoTenantSchema) });

export const AlterarFlagSchema = z
  .object({
    ligada: z.boolean(),
    /**
     * Obrigatória para LIGAR, e o servidor exige.
     *
     * O princípio 08 é que módulo, IA e conector só sejam ativados após
     * homologação — e `core.tenant_feature_flags.homologado_em` é `NOT NULL`,
     * então não existe linha ligada sem carimbo. A justificativa é o que o
     * carimbo registra.
     */
    justificativa: z.string().min(10).max(500),
  })
  .strict();

export type FlagDoTenant = z.infer<typeof FlagDoTenantSchema>;
export type FlagsDoTenant = z.infer<typeof FlagsDoTenantSchema>;
export type AlterarFlag = z.infer<typeof AlterarFlagSchema>;
